import React, {useState} from 'react'
import {
  BlogPost,
  useInsertPost,
  useRemovePost,
  useUpdatePost,
} from 'Event/Dashboard/components/BlogPosts'
import {useTemplate} from 'Event/TemplateProvider'
import {UseFormMethods, useForm} from 'react-hook-form'
import SettingsPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/SettingsPanel'
import RulesPanel from 'organization/Event/DashboardConfig/ComponentConfigPanel/RulesPanel'
import Settings from 'Event/Dashboard/components/BlogPosts/BlogPostConfig/Settings'
import ScheduleInput from 'organization/Event/DashboardConfig/ComponentConfigPanel/ScheduleInput'

import ComponentConfig, {
  ComponentConfigProps,
  SaveButton,
  RemoveButton,
  Footer,
} from 'organization/Event/DashboardConfig/ComponentConfigPanel'
import {Rule} from 'Event/attendee-rules'
import {
  useAutoUpdate,
  useTemplateEditor,
} from 'organization/Event/TemplateEditor'
import get from 'lodash/get'
import {useUserTranslations} from 'Event/LanguageProvider/translations'

export const DEFAULT_MODAL_BUTTON_TEXT = 'Submit'

export function EditPost(props: {id: string | null; onClose: () => void}) {
  const {id, onClose} = props
  const {blogPosts} = useTemplate()

  if (!id) {
    return null
  }

  const post = blogPosts[id]
  if (!post) {
    return null
  }

  return <BlogPostConfig id={id} showing post={post} onClose={onClose} />
}

interface BlogPostConfigProps<TPost extends BlogPost>
  extends ComponentConfigProps {
  post: TPost
  id?: string
  onUpdate?: (id: string, updated: Partial<TPost>) => void
  onInsert?: (post: TPost) => void
  onRemove?: (id: string) => void
  additionalInputs?: (params: {
    control: UseFormMethods['control']
  }) => JSX.Element
  inputNamePrefix?: string
}

export default function BlogPostConfig<TPost extends BlogPost>(
  props: BlogPostConfigProps<TPost>,
) {
  const {
    showing: visible,
    onClose,
    id,
    post,
    additionalInputs,
    onUpdate,
    onInsert,
    onRemove,
    inputNamePrefix = id ? `blogPosts.${id}.` : '',
  } = props

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: {isDirty},
    // Set to `any` type since the default generic Record<string, any> clashes with BlogPost.
    // Not a big deal since the default isn't type-safe anyway.
  } = useForm<any>()

  const translate = useUserTranslations()

  const [rules, setRules] = useState(post.rules)
  const [hasRulesChanges, setHasRulesChanges] = useState(false)

  const {clear: clearUpdates} = useTemplateEditor()

  // Have to watch date fields separately as it lives on the rules 'panel', and
  // when it's hidden on close, so is the `showingUntil` field. ie., the
  // value is never saved with form submit.

  const defaultUpdate = useUpdatePost()
  const update = onUpdate ?? defaultUpdate
  const defaultInsert = useInsertPost()
  const insert = onInsert ?? defaultInsert
  const defaultRemove = useRemovePost()
  const remove = onRemove ?? defaultRemove

  const close = () => {
    clearUpdates()
    onClose()
  }

  const save = (form: any) => {
    // Normalize the data by always returning a BlogPost even if a name
    // prefix was provided. The name prefix is used to set the correct
    // value so that useAutoUpdate() works as exptected.
    //
    const prefixWithoutTrailingDot = inputNamePrefix.slice(0, -1) // Prefixes end with a .so we need to v
    const formData = inputNamePrefix
      ? get(form, prefixWithoutTrailingDot)
      : form
    const data: TPost = {
      ...post,
      title: translate(post.title),
      content: translate(post.content),
      rules,
      ...formData,
    }

    if (id) {
      update(id, data)
      close()
      return
    }

    insert(data)
    close()
  }

  const handleRemove = () => {
    if (!id) {
      throw new Error(`Could not remove post; missing id.`)
    }

    close()
    remove(id)
  }

  const handleRules = (newRules: Rule[]) => {
    setHasRulesChanges(true)
    setRules(newRules)
  }

  useAutoUpdate({
    values: watch(),
    when: Boolean(id),
  })

  return (
    <ComponentConfig
      showing={visible}
      onClose={close}
      title="Blog Post"
      onSubmit={handleSubmit(save)}
      hasChanges={isDirty || hasRulesChanges}
      isDialogModal
    >
      <SettingsPanel>
        <Settings
          control={control}
          post={post}
          id={id}
          register={register}
          watch={watch}
          update={update}
          additionalInputs={additionalInputs}
          inputNamePrefix={inputNamePrefix}
        />
      </SettingsPanel>
      <RulesPanel rules={rules} setRules={handleRules}>
        <ScheduleInput
          control={control}
          setValue={setValue}
          fromName={`showingFrom`}
          untilName={`showingUntil`}
          values={{
            showingFrom: post.publishAt,
            showingUntil: post.showingUntil,
          }}
          inputNamePrefix={inputNamePrefix}
          disabledFrom
        />
      </RulesPanel>
      <Footer>
        <SaveButton type="submit" />
        <RemoveButton
          showing={Boolean(id)}
          fullWidth
          variant="outlined"
          aria-label="remove blog post"
          onClick={handleRemove}
          disabled={!id}
        />
      </Footer>
    </ComponentConfig>
  )
}
